import { uniq } from "lodash-es";
import { useNavigate } from "react-router";

import { LogoAws, Plug } from "components/icons/generated";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import SpacePath from "components/SpacePath";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import useCanDeleteScan from "shared/Scan/access/useCanDeleteScan";
import ScanRunStateBadge from "shared/Scan/StateBadge";
import TriggerScanButton from "shared/Scan/TriggerScanButton";
import { ScanProvider } from "types/generated";
import { IconComponent } from "types/Icon";
import { showDeleteConfirmation } from "views/Account/Scans/components/DeleteConfirmation";
import { SCAN_PROVIDER_LABEL } from "views/Account/Scans/components/Table/constants";
import { showScanDetailsDrawer } from "views/Account/Scans/DetailsDrawer";
import { SEARCH_SCAN_RUNS, SEARCH_SCAN_RUNS_COUNT } from "views/Account/Scans/ScanDetail/Scans/gql";

import { GET_SCAN_DETAIL } from "../../gql";
import { ScanDetailItem } from "../../types";

const SCAN_PROVIDER_ICON: Record<ScanProvider, IconComponent> = {
  [ScanProvider.Aws]: LogoAws,
};

type ScanDetailHeaderProps = {
  scan: ScanDetailItem;
};

const ScanDetailHeader = ({ scan }: ScanDetailHeaderProps) => {
  const navigate = useNavigate();
  const canDeleteScan = useCanDeleteScan()(scan);

  const handleDelete = () => {
    showDeleteConfirmation({ id: scan.id, name: scan.name }).then(() =>
      navigate("/resources/scans", { replace: true })
    );
  };

  const scanUrl = `/resources/scans/${scan.id}`;
  const providers = uniq(scan.providerConfigs.map(({ type }) => type));
  const integrationNames = uniq(
    scan.providerConfigs
      .map(({ integrationName }) => integrationName)
      .filter((integrationName): integrationName is string => !!integrationName)
  );

  return (
    <ViewHeader>
      <ViewHeaderWrapper direction="row" justify="between" fullWidth>
        <ViewHeaderWrapper direction="row" align="center">
          <ViewHeaderTitle>{scan.name}</ViewHeaderTitle>
          {scan.latestRun?.state && <ScanRunStateBadge state={scan.latestRun.state} />}
        </ViewHeaderWrapper>

        <ViewHeaderWrapper direction="row" align="end" shrink="0">
          <Button variant="secondary" onPress={() => showScanDetailsDrawer({ scan })}>
            Details
          </Button>

          <TriggerScanButton
            scan={scan}
            refetchQueries={[
              { query: GET_SCAN_DETAIL, variables: { id: scan.id } },
              SEARCH_SCAN_RUNS,
              SEARCH_SCAN_RUNS_COUNT,
            ]}
          />

          <DropdownMenuEllipsis dotsSize="medium" tooltip="Scan actions">
            <DropdownMenuItem href={`${scanUrl}/settings`}>Settings</DropdownMenuItem>
            {(canDeleteScan.result || canDeleteScan.message) && (
              <DropdownMenuItem
                onAction={handleDelete}
                isDisabled={!canDeleteScan.result}
                tooltip={canDeleteScan.message}
                tooltipPlacement="left"
                danger
              >
                Delete scan
              </DropdownMenuItem>
            )}
          </DropdownMenuEllipsis>
        </ViewHeaderWrapper>
      </ViewHeaderWrapper>

      <ViewHeaderScrollCollapse>
        <MetaInfoList>
          {scan.latestRun && (
            <TimestampMetaInfo prefix="Last scan" timestamp={scan.latestRun.stateChangedAt} />
          )}
          {providers.map((provider) => (
            <MetaInfoListItem key={provider} icon={SCAN_PROVIDER_ICON[provider]}>
              {SCAN_PROVIDER_LABEL[provider]}
            </MetaInfoListItem>
          ))}
          {integrationNames.map((integrationName) => (
            <MetaInfoListItem key={integrationName} icon={Plug} tooltip="Integration">
              {integrationName}
            </MetaInfoListItem>
          ))}
          <SpacePath variant="meta" spaceId={scan.space.id} fallbackName={scan.space.name} />
        </MetaInfoList>
      </ViewHeaderScrollCollapse>

      <ViewHeaderNavigation>
        <TabWithCounter
          end
          to={`${scanUrl}/resources`}
          count={scan.latestRun?.resourceCount ?? 0}
          label="Resources"
        />
        <Tab to={`${scanUrl}/scan-runs`} label="Scan runs" />
      </ViewHeaderNavigation>
    </ViewHeader>
  );
};

export default ScanDetailHeader;
